import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Package, Calendar, CheckCircle, Clock, XCircle, CreditCard } from 'lucide-react';
import { PageHeader, Button, Badge } from '@/components/shared/ui';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../lib/constants/routes';
import { useQuery, useQueries } from '@tanstack/react-query';
import { orderService } from '../../services/orderService';
import { orderKeys, ordersPageMyQueryKeyPrefix } from '../../hooks/useOrder';
import { ApiError } from '@/services/apiClient';
import type { OrderListItemDto, ApiOrderStatus, ApiPaymentStatus, GetOrderDetailResponseDto, PurchaseItemDto } from '../../types/api/order';
import {
    readRecentStudentOrderSummaries,
    pruneRecentStudentOrdersPresentInApi,
    pruneRecentStudentOrderSummariesOlderThan,
} from '@/lib/recentStudentOrders';
import {
    API_ORDER_STATUS_LABEL,
    API_ORDER_TYPE_LABEL,
    API_PAYMENT_STATUS_LABEL,
    API_PRODUCT_TYPE_LABEL,
    formatApiPaymentStatusForDisplay,
    getDisplayOrderStatusLabel,
    isOrderConfirmedForDisplay,
    type OrderStatusDisplayContext,
} from '../../types/api/order';
import { detectCurrencyFromOrderListRow, formatCurrency } from '@/lib/currencyFormat';
import { formatClassDateKoreanLong } from '@/utils/classUtils';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import { formatLocaleDateTime } from '@/lib/localeFormat';

export interface OrdersProps {
    /** 기관 ID (예: admin 기관 상세에서 전달). 미전달 시 현재 로그인 계정의 기관 ID 사용 */
    organizationId?: string | number;
    /** true면 페이지 레이아웃·타이틀 없이 내부 콘텐츠만 렌더 (예: OrgPage 주문 내역 탭) */
    embedMode?: boolean;
}

function orderStatusDisplayCtx(order: OrderListItemDto): OrderStatusDisplayContext {
    return {
        orderType: order.orderType,
        startAt: order.startAt,
        usedAt: order.usedAt,
    };
}

function getStatusBadgeInfo(order: OrderListItemDto) {
    const ctx = orderStatusDisplayCtx(order);
    const status = order.status;
    if (status === 'CONFIRMED') {
        const confirmed = isOrderConfirmedForDisplay(status, ctx);
        return {
            label: getDisplayOrderStatusLabel(status, ctx),
            icon: CheckCircle,
            className: confirmed ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700',
        };
    }
    switch (status) {
        case 'COMPLETED':
            return { label: API_ORDER_STATUS_LABEL[status], icon: CheckCircle, className: 'bg-green-100 text-green-700' };
        case 'IN_PROGRESS':
            return { label: API_ORDER_STATUS_LABEL[status], icon: Clock, className: 'bg-blue-100 text-blue-700' };
        case 'CANCELLED':
            return { label: API_ORDER_STATUS_LABEL[status], icon: XCircle, className: 'bg-gray-100 text-gray-600' };
        case 'DEMO':
            return { label: API_ORDER_STATUS_LABEL[status], icon: Clock, className: 'bg-yellow-100 text-yellow-700' };
        case 'FAILED':
            return { label: API_ORDER_STATUS_LABEL[status], icon: XCircle, className: 'bg-red-100 text-red-700' };
        default:
            return { label: String(status), icon: Clock, className: 'bg-gray-100 text-gray-600' };
    }
}

function formatOrderDate(dateString: string, locale: string): string {
    return formatLocaleDateTime(dateString, locale);
}

function formatYmdFromIso(iso: string | null | undefined): string {
    if (!iso?.trim()) return '';
    const s = iso.trim();
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
    return s.slice(0, 10);
}

function inclusiveCalendarDaysBetweenYmd(aYmd: string, bYmd: string): number {
    const s = new Date(`${aYmd}T12:00:00`);
    const e = new Date(`${bYmd}T12:00:00`);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 1;
    return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
}

/**
 * 온라인: 기간 일수(N일) / 대면·기관: 참여기간(한글 날짜 범위).
 * 오픈클래스 주문은 orderType이 ONLINE이어도 대면이면 isOnlineClass=false 로 내려와야 하며,
 * 미수신 시 기간이 2일 이상이면 참여기간(범위) 표기를 우선한다.
 */
function formatStudentOrderParticipationLine(
    order: OrderListItemDto,
    t: (key: string, opt?: Record<string, number>) => string,
    locale: string
): string | null {
    const a = formatYmdFromIso(order.startAt);
    const b = formatYmdFromIso(order.expiresAt);
    if (!a && !b) return null;

    const label = t('list.inPersonParticipationLabel');

    if (order.isOnlineClass === false || order.orderType === 'ORGANIZATION') {
        if (a && b) {
            return `${label}: ${formatClassDateKoreanLong(a, locale)} - ${formatClassDateKoreanLong(b, locale)}`;
        }
        return `${label}: ${formatClassDateKoreanLong((a || b) as string, locale)}`;
    }

    if (order.isOnlineClass === true) {
        if (a && b) {
            const days = inclusiveCalendarDaysBetweenYmd(a, b);
            return t('list.onlineParticipationDays', { count: days });
        }
        return `${t('list.participationPeriod')}: ${formatClassDateKoreanLong((a || b) as string, locale)}`;
    }

    // isOnlineClass === null: API 미표기 — 2일 이상이면 대면 기간(범위)으로 표시, 그 외는 하루 단위 문구
    if (a && b) {
        const days = inclusiveCalendarDaysBetweenYmd(a, b);
        if (days >= 2) {
            return `${label}: ${formatClassDateKoreanLong(a, locale)} - ${formatClassDateKoreanLong(b, locale)}`;
        }
        return `${label}: ${formatClassDateKoreanLong(a, locale)}`;
    }
    return `${label}: ${formatClassDateKoreanLong((a || b) as string, locale)}`;
}

function buildStudentOrderSubtitle(
    order: OrderListItemDto,
    orderTypeLabel: string,
    t: (key: string, opt?: Record<string, number>) => string,
    locale: string
): string {
    const org = order.organizationName?.trim() || '';
    const period = formatStudentOrderParticipationLine(order, t, locale);
    if (org && period) return `${org} | ${period}`;
    if (period) return period;
    if (org) return `${org} | ${orderTypeLabel}`;
    return orderTypeLabel;
}

function studentPaymentBadgeLabel(order: OrderListItemDto, t: (key: string) => string): string {
    const ctx = orderStatusDisplayCtx(order);
    if (order.paymentStatus === 'PAID') return API_PAYMENT_STATUS_LABEL.PAID;
    if (order.paymentStatus === 'REFUNDED') return API_PAYMENT_STATUS_LABEL.REFUNDED;
    /**
     * `/order/my`에 `paymentStatus`가 없으면 UN_INVOICED로만 매핑되는 경우가 있어
     * 주문이 이미 CONFIRMED인데도 「결제 진행중」으로 보이는 문제가 난다. 주문 상태를 우선한다.
     */
    if (order.status === 'CONFIRMED' || order.status === 'COMPLETED') {
        return getDisplayOrderStatusLabel(order.status, ctx);
    }
    if (order.status === 'CANCELLED' || order.status === 'FAILED') {
        return API_ORDER_STATUS_LABEL[order.status];
    }
    return t('list.statusPaymentInProgress');
}

function studentPaymentBadgeClassName(order: OrderListItemDto): string {
    if (order.paymentStatus === 'PAID') {
        return 'bg-emerald-50 text-emerald-800 border border-emerald-100';
    }
    if (order.paymentStatus === 'REFUNDED') {
        return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
    if (order.status === 'CONFIRMED') {
        return isOrderConfirmedForDisplay('CONFIRMED', orderStatusDisplayCtx(order))
            ? 'bg-purple-50 text-purple-800 border border-purple-100'
            : 'bg-green-100 text-green-700 border border-green-100';
    }
    if (order.status === 'COMPLETED') {
        return 'bg-green-100 text-green-700 border border-green-100';
    }
    if (order.status === 'CANCELLED' || order.status === 'FAILED') {
        return 'bg-gray-100 text-gray-600 border border-gray-200';
    }
    return 'bg-sky-50 text-sky-800 border border-sky-100';
}

/** 목록 DTO에서 품목/요약 행 라벨 (ux-design 주문 카드 품목 구역과 유사) */
function productLineLabels(order: OrderListItemDto): string[] {
    const names = order.productNames;
    if (Array.isArray(names) && names.length > 0) {
        return names.map((s) => String(s).trim()).filter(Boolean);
    }
    const single = order.productName?.trim();
    if (single) {
        return single.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
}

function productTypeSummary(order: OrderListItemDto): string {
    const types = order.productTypes ?? [];
    if (types.length === 0) return '';
    return types.map((pt) => API_PRODUCT_TYPE_LABEL[pt] ?? pt).join(', ');
}

/** localStorage 합성 행: API 지연·id 교체 후에도 유령 카드가 무한히 쌓이지 않도록 TTL */
const RECENT_STUDENT_SYNTH_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** 동일 orderId 중복 행 제거(최신 createdAt 우선) */
function dedupeOrdersByOrderId(rows: OrderListItemDto[]): OrderListItemDto[] {
    const m = new Map<number, OrderListItemDto>();
    for (const r of rows) {
        const prev = m.get(r.orderId);
        if (!prev || new Date(r.createdAt).getTime() >= new Date(prev.createdAt).getTime()) {
            m.set(r.orderId, r);
        }
    }
    return [...m.values()];
}

/** 동일 시각·동일 기관으로 여러 orderId/주문번호가 내려오는 경우(복수 오픈클래스 한 건 분할 등) 한 카드로 병합 */
function mergeOrdersByCreatedAtOrgInstant(rows: OrderListItemDto[]): OrderListItemDto[] {
    const groups = new Map<string, OrderListItemDto[]>();
    for (const r of rows) {
        // TODO: API 연결 미흡 - 기관 식별자 필드명이 불확실하여 여러 키를 시도
        const orgKey =
            String(r.organizationId ?? r.organizationName ?? r.userName ?? '').trim() || '_';
        const k = `${orgKey}|${r.createdAt}`;
        const arr = groups.get(k) ?? [];
        arr.push(r);
        groups.set(k, arr);
    }
    const out: OrderListItemDto[] = [];
    for (const [, group] of groups) {
        if (group.length === 1) {
            out.push(group[0]);
            continue;
        }
        const distinctIds = new Set(group.map((g) => g.orderId));
        if (distinctIds.size <= 1) {
            out.push(group[0]);
            continue;
        }
        out.push(mergeOrderListGroup(group, 'sum'));
    }
    return out;
}

function mergeOrderListGroup(group: OrderListItemDto[], totalAmountMode: 'sum' | 'max'): OrderListItemDto {
    group.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const names = new Set<string>();
    for (const g of group) {
        for (const line of productLineLabels(g)) names.add(line);
    }
    const mergedNames = [...names];
    const primary = group[0];
    const mergedIsOnline =
        group.some((g) => g.isOnlineClass === false)
            ? false
            : group.some((g) => g.isOnlineClass === true)
              ? true
              : primary.isOnlineClass;
    const totalAmount =
        totalAmountMode === 'sum'
            ? group.reduce((acc, g) => acc + g.totalAmount, 0)
            : group.reduce((acc, g) => Math.max(acc, g.totalAmount), 0);
    return {
        ...primary,
        productNames: mergedNames.length > 0 ? mergedNames : primary.productNames,
        productName: mergedNames.length > 0 ? mergedNames.join(', ') : primary.productName,
        totalAmount,
        ...(mergedIsOnline !== undefined && mergedIsOnline !== null ? { isOnlineClass: mergedIsOnline } : {}),
    };
}

/** 동일 주문번호로 여러 행이 내려온 경우(복수 클래스 분할 등) 한 카드로 병합 */
function mergeOrdersByOrderNumber(rows: OrderListItemDto[]): OrderListItemDto[] {
    const groups = new Map<string, OrderListItemDto[]>();
    for (const r of rows) {
        const k = (r.orderNumber || '').trim() || `__id_${r.orderId}`;
        const arr = groups.get(k) ?? [];
        arr.push(r);
        groups.set(k, arr);
    }
    const out: OrderListItemDto[] = [];
    for (const [, group] of groups) {
        if (group.length === 1) {
            out.push(group[0]);
            continue;
        }
        // 복수 상품을 한 카드로 병합하되, 원본 그룹 정보 보존
        const merged = mergeOrderListGroup(group, 'max');
        // @ts-ignore - subOrders는 런타임에만 추가되는 필드
        merged.subOrders = group;
        out.push(merged);
    }
    return out;
}

function dedupeAndMergeOrderRows(rows: OrderListItemDto[]): OrderListItemDto[] {
    // API가 이미 purchaseItems 배열로 여러 제품을 반환하므로 병합 불필요
    const step1 = dedupeOrdersByOrderId(rows);
    return step1.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 * GET /order/my 행에 기관·주문자·상품명이 하나도 없으면 “저장이 안 된 것처럼” 보임.
 * 이때는 주문·결제 직전 넣은 localStorage 요약을 유지해야 하므로, 이런 행은 prune 대상에서 제외한다.
 */
function orderListRowHasUsableDisplayFields(o: OrderListItemDto): boolean {
    const org = o.organizationName?.trim();
    const user = o.userName?.trim();
    const pn = o.productName?.trim();
    const pns = o.productNames && o.productNames.length > 0;
    return Boolean(org || user || pn || pns);
}

function pickTrimmedString(v: unknown): string {
    if (typeof v !== 'string') return '';
    return v.trim();
}

/** 상세 items 행 — camelCase·snake_case·클래스 중첩 title 등 백엔드 변형 대응 */
function displayLineFromOrderDetailItemRaw(raw: unknown): string | null {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const it = raw as Record<string, unknown>;
    let name =
        pickTrimmedString(it.productName) ||
        pickTrimmedString(it.product_name) ||
        pickTrimmedString(it.productTitle) ||
        pickTrimmedString(it.product_title) ||
        pickTrimmedString(it.productPriceName) ||
        pickTrimmedString(it.product_price_name) ||
        pickTrimmedString(it.title) ||
        pickTrimmedString(it.classTitle) ||
        pickTrimmedString(it.class_title) ||
        pickTrimmedString(it.itemName) ||
        pickTrimmedString(it.item_name) ||
        pickTrimmedString(it.contentTitle) ||
        pickTrimmedString(it.content_title);
    if (!name) {
        const prod = it.product;
        if (prod && typeof prod === 'object' && !Array.isArray(prod)) {
            const p = prod as Record<string, unknown>;
            name =
                pickTrimmedString(p.productTitle) ||
                pickTrimmedString(p.title) ||
                pickTrimmedString(p.name) ||
                '';
        }
    }
    if (!name) {
        for (const nk of ['class', 'classInfo', 'openClass', 'orderClass'] as const) {
            const nested = it[nk];
            if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
                const o = nested as Record<string, unknown>;
                name =
                    pickTrimmedString(o.title) ||
                    pickTrimmedString(o.classTitle) ||
                    pickTrimmedString(o.class_title) ||
                    pickTrimmedString(o.className) ||
                    pickTrimmedString(o.name) ||
                    '';
                if (name) break;
            }
        }
    }
    if (!name) return null;
    const qRaw = it.quantity;
    let q = 1;
    if (typeof qRaw === 'number' && Number.isFinite(qRaw) && qRaw > 0) q = Math.floor(qRaw);
    else if (typeof qRaw === 'string' && /^\d+$/.test(qRaw.trim())) {
        const n = parseInt(qRaw.trim(), 10);
        if (Number.isFinite(n) && n > 0) q = n;
    }
    return q > 1 ? `${name} ×${q}` : name;
}

function productDisplayLinesFromOrderDetail(detail: GetOrderDetailResponseDto): string[] {
    const items = detail.items as unknown;
    if (!Array.isArray(items)) return [];
    const out: string[] = [];
    for (const raw of items) {
        const line = displayLineFromOrderDetailItemRaw(raw);
        if (line) out.push(line);
    }
    return out;
}

/** GET /order/{id}/detail 루트에만 있는 상품·클래스명 (품목 배열이 비어도 제목 표시용) */
function pickProductTitleFromOrderDetailRoot(detail: GetOrderDetailResponseDto): string | null {
    const r = detail as unknown as Record<string, unknown>;
    const t =
        pickTrimmedString(r.productTitle) ||
        pickTrimmedString(r.product_title) ||
        pickTrimmedString(r.classTitle) ||
        pickTrimmedString(r.class_title) ||
        pickTrimmedString(r.orderTitle) ||
        pickTrimmedString(r.order_title);
    return t || null;
}

/**
 * 주문 품목에 붙은 클래스 수강기간(클래스 상세의 수강기간과 동일해야 함).
 * 목록의 startAt/expiresAt 는 라이선스·모집일 등과 엇갈릴 수 있어, 상세에서는 품목·중첩 class 우선.
 */
function pickClassPeriodYmdPairFromRecord(o: Record<string, unknown>): { start: string; end: string } | null {
    // TODO: API 연결 미흡 - 날짜 필드명이 camelCase와 snake_case 혼용되어 여러 키를 시도
    const rawA = o.classStartDate ?? o.class_start_date ?? o.startDate ?? o.start_date;
    const rawB = o.classEndDate ?? o.class_end_date ?? o.endDate ?? o.end_date;
    const a = formatYmdFromIso(rawA != null && rawA !== '' ? String(rawA) : undefined);
    const b = formatYmdFromIso(rawB != null && rawB !== '' ? String(rawB) : undefined);
    if (a && b) return { start: a, end: b };
    if (a && !b) return { start: a, end: a };
    return null;
}

function pickClassPeriodFromOrderItemRaw(raw: unknown): { start: string; end: string } | null {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const it = raw as Record<string, unknown>;
    const direct = pickClassPeriodYmdPairFromRecord(it);
    if (direct) return direct;
    for (const nk of ['class', 'classInfo', 'openClass', 'orderClass'] as const) {
        const n = it[nk];
        if (n && typeof n === 'object' && !Array.isArray(n)) {
            const p = pickClassPeriodYmdPairFromRecord(n as Record<string, unknown>);
            if (p) return p;
        }
    }
    return null;
}

/** 품목들에 수강기간이 여러 개면 시작일 최소·종료일 최대으로 합침 */
function classParticipationPeriodIsoFromOrderDetail(detail: GetOrderDetailResponseDto): { startAt: string; expiresAt: string } | null {
    const items = detail.items as unknown;
    if (!Array.isArray(items) || items.length === 0) return null;
    const periods = items
        .map((raw) => pickClassPeriodFromOrderItemRaw(raw))
        .filter((x): x is { start: string; end: string } => x != null);
    if (periods.length === 0) return null;
    if (periods.length === 1) {
        return {
            startAt: `${periods[0].start}T12:00:00.000Z`,
            expiresAt: `${periods[0].end}T12:00:00.000Z`,
        };
    }
    const starts = periods.map((p) => p.start).sort();
    const ends = periods.map((p) => p.end).sort();
    return {
        startAt: `${starts[0]}T12:00:00.000Z`,
        expiresAt: `${ends[ends.length - 1]}T12:00:00.000Z`,
    };
}

/** GET /order/{id}/detail 로 목록 행 보강 — 학생 `/order/my` 가 희박할 때 */
function mergeListRowWithDetail(
    row: OrderListItemDto,
    detail: GetOrderDetailResponseDto | null | undefined
): OrderListItemDto {
    if (!detail) return row;
    const fromItems = productDisplayLinesFromOrderDetail(detail);
    const fromClassPeriod = classParticipationPeriodIsoFromOrderDetail(detail);
    const fromRootTitle = pickProductTitleFromOrderDetailRoot(detail);
    const productNamesMerged =
        row.productNames && row.productNames.length > 0
            ? row.productNames
            : fromItems.length > 0
              ? fromItems
              : fromRootTitle
                ? [fromRootTitle]
                : row.productNames;
    const productNameMerged =
        row.productName?.trim() ||
        (productNamesMerged && productNamesMerged.length > 0 ? productNamesMerged.join(', ') : null) ||
        fromRootTitle ||
        row.productName;
    const ps = detail.paymentStatus;
    const paymentMerged: ApiPaymentStatus =
        ps === 'UN_INVOICED' || ps === 'INVOICED' || ps === 'PAID' || ps === 'REFUNDED' ? ps : row.paymentStatus;
    return {
        ...row,
        organizationName: row.organizationName?.trim() || detail.organizationName?.trim() || row.organizationName,
        userName: row.userName?.trim() || detail.userName?.trim() || row.userName,
        productNames: productNamesMerged ?? row.productNames,
        productName: productNameMerged,
        paymentStatus: paymentMerged,
        startAt: fromClassPeriod?.startAt ?? detail.startAt ?? row.startAt,
        expiresAt: fromClassPeriod?.expiresAt ?? detail.expiresAt ?? row.expiresAt,
        usedAt: row.usedAt ?? detail.usedAt,
        isOnlineClass: row.isOnlineClass ?? detail.isOnlineClass,
    };
}

/**
 * 도메인: `/student/orders` = 학생 본인 구매 내역(포털 GNB). 목록 API는 GET `/order/my` 분기와 짝.
 * (`/master/organization?section=orders` 기관 주문 처리는 OrderManagement + GET `/order/list` — 이 컴포넌트 아님.)
 */
function isStudentOrdersAppPath(pathname: string): boolean {
    const ordersPath = `${ROUTES.STUDENT.BASE}/orders`;
    const p = pathname.replace(/\/+$/, '') || '/';
    return p === ordersPath || p.startsWith(`${ordersPath}/`);
}

/**
 * 도메인: `/master/orders` = 마스터 본인 오픈클래스 등 **개인 구매** 내역. 기관 매출/처리용 주문 탭과 구분.
 * 목록 API: GET `/order/my` (`useOrderMyListApi`).
 */
function isMasterOrdersAppPath(pathname: string): boolean {
    const ordersPath = `${ROUTES.MASTER.BASE}/orders`;
    const p = pathname.replace(/\/+$/, '') || '/';
    return p === ordersPath || p.startsWith(`${ordersPath}/`);
}

export default function Orders({ organizationId: propOrganizationId, embedMode = false }: OrdersProps = {}): React.ReactElement {
    const { t, i18n } = useTranslation('order');
    const navigate = useNavigate();
    const location = useLocation();
    const apiLang = useApiLanguage();
    const { user } = useAuth();
    const role = user?.currentAccount?.role ?? 'visitor';
    const [page] = useState(1);

    const selfOrgId = user?.currentAccount?.organizationId;

    /** API는 `organizationId`를 숫자로 기대 — 계정에는 문자열(예: "ORG001")로 올 수 있음 */
    const parsedSelfOrgId = useMemo(() => {
        if (selfOrgId == null || selfOrgId === '') return null;
        const n = typeof selfOrgId === 'number' ? selfOrgId : Math.floor(Number(selfOrgId));
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [selfOrgId]);

    const roleNorm = String(role).toLowerCase();
    const useOrgWideOrderListApi = roleNorm === 'master' || roleNorm === 'admin';

    /**
     * 학생·게스트 등은 `/order/my`. `/student/orders`는 역할 표기와 무관하게 본인 주문 UX(최근 주문 병합 등).
     * `/master/orders` 포털도 동일하게 GET `/order/my` 로 조회.
     */
    const useMyOrdersApi =
        !embedMode && (!useOrgWideOrderListApi || isStudentOrdersAppPath(location.pathname));

    /** 비임베드에서 GET `/order/my` — 학생·게스트 맥락 + 마스터 포털 `/master/orders` */
    const useOrderMyListApi =
        !embedMode && (useMyOrdersApi || isMasterOrdersAppPath(location.pathname));

    const orderListRequestParams = useMemo(
        () => ({
            page,
            pageSize: 50 as const,
            ...(parsedSelfOrgId != null ? { organizationId: parsedSelfOrgId } : {}),
        }),
        [page, parsedSelfOrgId]
    );

    const { data, isLoading, error } = useQuery({
        queryKey: embedMode
            ? (['orders', 'list', 'embed', page] as const)
            : useOrderMyListApi
              ? ([...ordersPageMyQueryKeyPrefix, page, apiLang] as const)
              : orderKeys.list(orderListRequestParams),
        queryFn: () => {
            /**
             * 임베드: `Orders`를 카드형으로 넣은 화면 — GET `/order/list`.
             * 마스터는 토큰의 organizationId로 자기 기관 주문만 조회.
             */
            if (embedMode) {
                return orderService.getList({ page, pageSize: 50 });
            }
            if (useOrderMyListApi) {
                return orderService.getMyList({ page, pageSize: 50, language: apiLang });
            }
            return orderService.getList(orderListRequestParams);
        },
        staleTime: 30000,
    });

    const orders: OrderListItemDto[] = data?.orders ?? [];

    const mergedOrders = useMemo(() => {
        const recent = readRecentStudentOrderSummaries();
        const now = Date.now();
        const recentEligibleForSynth = recent.filter((r) => {
            const t = new Date(r.createdAt).getTime();
            return Number.isFinite(t) && now - t <= RECENT_STUDENT_SYNTH_MAX_AGE_MS;
        });
        const recentById = new Map(recent.map((r) => [r.orderId, r]));

        const enrichFromRecent = (o: OrderListItemDto): OrderListItemDto => {
            const r = recentById.get(o.orderId);
            if (!r) return o;
            const org = o.organizationName?.trim() || r.organizationName?.trim() || null;
            const user = o.userName?.trim() || r.userName?.trim() || null;
            const pNames =
                o.productNames && o.productNames.length > 0
                    ? o.productNames
                    : r.productNames && r.productNames.length > 0
                      ? r.productNames
                      : o.productNames;
            const pLine =
                o.productName?.trim() ||
                (pNames && pNames.length > 0 ? pNames.join(', ') : null) ||
                o.productName;
            return {
                ...o,
                organizationName: org,
                userName: user,
                productNames: pNames ?? o.productNames,
                productName: pLine,
            };
        };

        if (!useMyOrdersApi) {
            return dedupeAndMergeOrderRows(orders.map(enrichFromRecent));
        }

        const apiOrders = orders.map(enrichFromRecent);
        const apiIds = new Set(apiOrders.map((o) => o.orderId));
        const apiOrderNumbers = new Set(
            apiOrders.map((o) => (o.orderNumber || '').trim()).filter((s) => s.length > 0)
        );
        const synth: OrderListItemDto[] = recentEligibleForSynth
            .filter((r) => {
                if (r.orderId <= 0 || apiIds.has(r.orderId)) return false;
                const n = String(r.orderNumber || '').trim();
                if (n && apiOrderNumbers.has(n)) return false;
                return true;
            })
            .map((r) => {
                const ps: ApiPaymentStatus =
                    r.paymentStatus === 'UN_INVOICED' ||
                    r.paymentStatus === 'INVOICED' ||
                    r.paymentStatus === 'PAID' ||
                    r.paymentStatus === 'REFUNDED'
                        ? r.paymentStatus
                        : 'UN_INVOICED';
                const pNames = r.productNames && r.productNames.length > 0 ? r.productNames : null;
                return {
                    orderId: r.orderId,
                    orderNumber: r.orderNumber,
                    orderType: 'ONLINE' as const,
                    userName: r.userName ?? null,
                    productTypes: [],
                    regularPriceTotal: r.totalAmount,
                    discountedPriceTotal: r.totalAmount,
                    totalAmount: r.totalAmount,
                    status: 'IN_PROGRESS' as ApiOrderStatus,
                    paymentStatus: ps,
                    isLicenseActive: false,
                    createdAt: r.createdAt,
                    organizationName: r.organizationName ?? null,
                    productNames: pNames,
                    productName: pNames ? pNames.join(', ') : null,
                };
            });
        const combined = [...synth, ...apiOrders].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        return dedupeAndMergeOrderRows(combined);
    }, [orders, useMyOrdersApi]);

    useEffect(() => {
        pruneRecentStudentOrderSummariesOlderThan();
    }, []);

    useEffect(() => {
        if (!useMyOrdersApi || orders.length === 0) return;
        pruneRecentStudentOrdersPresentInApi(
            orders.map((o) => ({ orderId: o.orderId, orderNumber: o.orderNumber }))
        );
    }, [orders, useMyOrdersApi]);

    const masterOrdersPortal = !embedMode && isMasterOrdersAppPath(location.pathname);

    // TODO: 용도에 맞는 API인지 확인 필요
    // 문제: 주문 목록 API에 표시 필드(organizationName, userName, 판매처 등)가 없음
    // → 목록 API 1개 + 각 주문마다 상세 API N개 호출 (N+1 문제)
    const orderDetailQueries = useQueries({
        queries: mergedOrders.map((o) => ({
            queryKey: [...orderKeys.detail(o.orderId), apiLang, 'listEnrichment'],
            queryFn: () => orderService.getDetailForListEnrichment(o.orderId, apiLang),
            /**
             * `/master/orders`: `GET /order/my`만 사용 — `GET /order/{id}/detail` 목록 보강은 호출하지 않음(403/불필요 요청 방지).
             * 그 외: 표시필드가 비어 있을 때만 `detail`로 보강.
             */
            enabled:
                !masterOrdersPortal &&
                mergedOrders.length > 0 &&
                o.orderId > 0 &&
                !orderListRowHasUsableDisplayFields(o),
            staleTime: 60_000,
            retry: (failureCount: number, err: unknown) => {
                if (err instanceof ApiError && (err.status === 403 || err.status === 401)) return false;
                return failureCount < 1;
            },
        })),
    });

    const content = (
        <>
            {isLoading ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">불러오는 중...</div>
            ) : error ? (
                <div className="bg-red-50 rounded-lg p-8 text-center text-red-600">
                    <p className="font-medium">주문 내역을 불러오지 못했습니다.</p>
                    {error instanceof Error && error.message ? (
                        <p className="mt-2 text-sm text-red-700/90 break-words">{error.message}</p>
                    ) : null}
                </div>
            ) : mergedOrders.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 sm:p-12 text-center">
                    <Package size={48} className="mx-auto mb-3 sm:mb-4 text-gray-300 sm:w-16 sm:h-16" />
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">
                        {t('list.noOrders')}
                    </h3>
                    {useMyOrdersApi && (
                        <p className="text-sm text-gray-500 max-w-md mx-auto mb-4 leading-relaxed">
                            {t('list.emptyHint')}
                        </p>
                    )}
                    {!embedMode && (
                        <Button variant="primary" onClick={() => navigate(ROUTES.COMMON.CLASS_LIST)}>
                            {t('list.browseClasses')}
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-4 sm:space-y-6 rounded-lg">
                    {mergedOrders.map((rawOrder, orderIndex) => {
                        const detail = orderDetailQueries[orderIndex]?.data;
                        const order = mergeListRowWithDetail(rawOrder, detail);
                        const titleFromDetailOnly = detail ? pickProductTitleFromOrderDetailRoot(detail) : null;
                        const statusInfo = getStatusBadgeInfo(order);
                        const StatusIcon = statusInfo.icon;
                        const productTypeLabels = productTypeSummary(order);
                        const paymentLabel = formatApiPaymentStatusForDisplay(
                            order.paymentStatus,
                            t('management.paymentStatusNA')
                        );
                        const amountCurrency = detectCurrencyFromOrderListRow(order, detail ?? null);
                        const lines = productLineLabels(order);
                        const orderTypeLabel = API_ORDER_TYPE_LABEL[order.orderType] ?? String(order.orderType);
                        const productTitleFallback = lines.join(', ').trim() || order.productName?.trim() || '';
                        /** 임베드(기관 탭) 전용 — 상품 행이 비었을 때만 안내 문구 */
                        const partyFallback =
                            order.organizationName?.trim() ||
                            order.userName?.trim() ||
                            productTitleFallback ||
                            (productTypeLabels ? productTypeLabels : '') ||
                            t('list.embedUnknownParty');

                        const primaryProductTitle =
                            lines.length > 0
                                ? lines[0]
                                : order.productName?.trim() ||
                                  productTitleFallback ||
                                  titleFromDetailOnly?.trim() ||
                                  order.organizationName?.trim() ||
                                  order.userName?.trim() ||
                                  (productTypeLabels ? productTypeLabels : '') ||
                                  order.orderNumber ||
                                  '';
                        const studentSubtitle = buildStudentOrderSubtitle(order, orderTypeLabel, t, i18n.language);
                        const studentOrderCard = !embedMode && useMyOrdersApi;

                        return (
                            <div
                                key={order.orderId}
                                className="rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm"
                            >
                                <div className="px-4 sm:px-6 pt-3 sm:pt-4">
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                        <div className="flex flex-col items-start gap-0.5 min-w-0">
                                            <div className="flex items-center gap-2 text-sm sm:text-base font-bold text-gray-700">
                                                <Calendar size={16} className="shrink-0 text-gray-500" />
                                                <span>{formatOrderDate(order.createdAt, i18n.language)}</span>
                                            </div>
                                            {!embedMode && (
                                                <span className="text-xs text-gray-400 font-normal">{order.orderNumber}</span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 justify-end sm:ml-auto">
                                            {embedMode && productTypeLabels && (
                                                <Badge
                                                    variant="secondary"
                                                    className="text-xs font-medium text-gray-600 bg-gray-100"
                                                >
                                                    {productTypeLabels}
                                                </Badge>
                                            )}
                                            {!studentOrderCard && !embedMode && productTypeLabels && (
                                                <Badge
                                                    variant="secondary"
                                                    className="text-xs font-medium text-gray-600 bg-gray-100"
                                                >
                                                    {productTypeLabels}
                                                </Badge>
                                            )}
                                            {studentOrderCard ? (
                                                <span
                                                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-semibold ${studentPaymentBadgeClassName(order)}`}
                                                >
                                                    {studentPaymentBadgeLabel(order, t)}
                                                </span>
                                            ) : (
                                                <Badge
                                                    className={`flex items-center text-xs sm:text-sm font-medium gap-1 ${statusInfo.className}`}
                                                >
                                                    <StatusIcon size={14} />
                                                    {statusInfo.label}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {embedMode ? (
                                    <>
                                        <div className="px-4 sm:px-6 py-2 sm:py-3">
                                            {lines.length > 0 ? (
                                                lines.map((line, idx) => (
                                                    <div
                                                        key={`${order.orderId}-line-${idx}`}
                                                        className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50/80"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm sm:text-base font-bold text-gray-900">{line}</p>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-3 hover:bg-gray-50/80">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm sm:text-base font-bold text-gray-900">{partyFallback}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="px-4 sm:px-6 pb-3 sm:pb-4 pt-1 bg-gray-50/60 border-t border-gray-100">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                                <div className="flex items-center gap-2 text-sm sm:text-base text-gray-600">
                                                    <CreditCard size={16} className="shrink-0 text-gray-500" />
                                                    <span>{paymentLabel}</span>
                                                </div>
                                                <div className="text-left sm:text-right">
                                                    <span className="text-xs sm:text-sm text-gray-600 mr-2">{t('list.totalAmount')}</span>
                                                    <span className="text-base sm:text-lg font-bold text-gray-900 tabular-nums">
                                                        {formatCurrency(order.totalAmount, amountCurrency)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : studentOrderCard ? (
                                    <div className="px-4 sm:px-6 pb-4 sm:pb-5">
                                        {order.purchaseItems && order.purchaseItems.length > 1 ? (
                                            <>
                                                {/* 복수 상품 표시 */}
                                                {order.purchaseItems.map((item: PurchaseItemDto, idx: number) => {
                                                    const itemCurrency = detectCurrencyFromOrderListRow(order, detail ?? null);
                                                    return (
                                                        <div
                                                            key={`${order.orderId}-item-${idx}`}
                                                            className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 py-4 border-b border-gray-100"
                                                        >
                                                            <div className="flex-1 min-w-0 text-left">
                                                                <p className="text-base sm:text-lg font-bold text-gray-900">
                                                                    {item.title}
                                                                </p>
                                                                <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                                                                    {studentSubtitle}
                                                                </p>
                                                            </div>
                                                            <div className="text-left sm:text-right shrink-0">
                                                                <p className="text-lg sm:text-xl font-semibold text-gray-900 tabular-nums">
                                                                    {formatCurrency(item.price, itemCurrency)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {/* 전체 합계 */}
                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4">
                                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                                        <CreditCard size={16} className="shrink-0 text-gray-500" />
                                                        <span>{t('list.paymentMethodCard')}</span>
                                                    </div>
                                                    <div className="text-left sm:text-right">
                                                        <span className="text-xs sm:text-sm text-gray-500 mr-2">
                                                            {t('list.totalAmount')}
                                                        </span>
                                                        <span className="text-base sm:text-lg font-bold text-gray-900 tabular-nums">
                                                            {formatCurrency(order.totalAmount, amountCurrency)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {/* 단일 상품 표시 (기존 로직) */}
                                                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 py-4 border-b border-gray-100">
                                                    <div className="flex-1 min-w-0 text-left">
                                                        <p className="text-base sm:text-lg font-bold text-gray-900">
                                                            {primaryProductTitle}
                                                        </p>
                                                        <p className="text-xs sm:text-sm text-gray-500 mt-1 leading-relaxed">
                                                            {studentSubtitle}
                                                        </p>
                                                    </div>
                                                    <div className="text-left sm:text-right shrink-0">
                                                        <p className="text-lg sm:text-xl font-semibold text-gray-900 tabular-nums">
                                                            {formatCurrency(order.totalAmount, amountCurrency)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4">
                                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                                        <CreditCard size={16} className="shrink-0 text-gray-500" />
                                                        <span>{t('list.paymentMethodCard')}</span>
                                                    </div>
                                                    <div className="text-left sm:text-right">
                                                        <span className="text-xs sm:text-sm text-gray-500 mr-2">
                                                            {t('list.totalAmount')}
                                                        </span>
                                                        <span className="text-base sm:text-lg font-bold text-gray-900 tabular-nums">
                                                            {formatCurrency(order.totalAmount, amountCurrency)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="px-4 sm:px-6 py-2 sm:py-3">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 py-3">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm sm:text-base font-bold text-gray-900">
                                                    {order.organizationName?.trim() ||
                                                        order.userName?.trim() ||
                                                        productTitleFallback ||
                                                        order.orderNumber ||
                                                        t('list.embedUnknownParty')}
                                                </p>
                                                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{orderTypeLabel}</p>
                                            </div>
                                            <div className="text-left sm:text-right">
                                                <p className="text-sm sm:text-base font-semibold text-gray-900">
                                                    {formatCurrency(order.totalAmount, amountCurrency)}
                                                </p>
                                                <p className="text-xs text-gray-500">{paymentLabel}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {!embedMode && (
                <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-base sm:text-lg font-bold text-brand-600 mb-2 sm:mb-3">{t('list.cancelPolicyTitle')}</h3>
                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-700 leading-relaxed">
                        <p>• {t('list.cancelPolicy1')}</p>
                        <p>• {t('list.cancelPolicy2')}</p>
                        <p>• {t('list.cancelPolicy3')}</p>
                        <p>• {t('list.cancelPolicy4')}</p>
                        <p>• {t('list.cancelPolicy5')}</p>
                    </div>
                </div>
            )}
        </>
    );

    if (embedMode) {
        return <div className="w-full">{content}</div>;
    }

    return (
        <div className="max-w-[1024px] mx-auto px-4 py-8">
            <PageHeader
                title={t('list.pageTitle')}
            />
            <p className="mt-4 text-base text-gray-900 leading-relaxed">
                <Trans
                    i18nKey="list.receiptNotice"
                    ns="order"
                    components={{
                        contact: (
                            <Link
                                to={ROUTES.COMMON.CONTACT}
                                className="font-medium text-brand-600 underline-offset-2 hover:underline"
                            />
                        ),
                    }}
                />
            </p>
            <div className="mt-6">{content}</div>
        </div>
    );
}
